import React from 'react';
import DialogModal from 'tradera-components/dialog/dialog-modal';
import ModalContainer from 'static/script/components/modals/modal-container';
import ModalHeader from 'static/script/components/modals/modal-header';
import ModalBody from 'static/script/components/modals/modal-body';
import { connect } from 'react-redux';
import { useTranslator, useUrlLocalizer } from 'tradera-lang/translate';
import { closeModal } from 'tradera-state/ui/action-creators';
import { mapCountryCodeToCountryKey } from 'tradera-utils/countries';
import { selectShippingCountry } from 'tradera-state/shipping-region/selectors';
import { ShippingProviderIcon } from 'tradera-components/shipping/shipping-provider-icon';
import MessagingButton from 'tradera-components/messaging/messaging-button';
import Flag from 'tradera-components/flag/flag';
import { translateBuyerRegionText } from 'tradera-apps/tradera/components/vip-shared/shipping-options/translate-buyer-region-text';
import styles from './international-shipping-modal.module.scss';

const InternationalShippingModal = props => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    let sortedRows = props.internationalShipping.rows
        .map(row => ({
            ...row,
            country: t(mapCountryCodeToCountryKey(row.toCountryCodeIso2))
        }))
        .sort((a, b) => (a.country > b.country ? 1 : -1));
    const ownCountryRow = sortedRows.find(
        row =>
            row.toCountryCodeIso2.toLowerCase() ===
            props.shippingCountryCode.toLowerCase()
    ) || {
        country: t(mapCountryCodeToCountryKey(props.shippingCountryCode)),
        toCountryCodeIso2: props.shippingCountryCode
    };
    sortedRows = removeDuplicateOwnCountryRowIfOnlyRow(
        props.shippingCountryCode,
        sortedRows
    );

    const buyerRegionText = translateBuyerRegionText(
        t,
        props.internationalShipping.allowedBuyerRegion
    );

    return (
        <Modal
            closeModal={props.closeModal}
            header={
                <>
                    <div>{t('internationalShippingModal_title')}</div>
                    <div className="size-paris d-flex justify-content-between align-items-center">
                        <span
                            className="text-truncate"
                            title={props.internationalShipping.title}>
                            <img src={props.internationalShipping.imageUrl} />
                            <span>{props.internationalShipping.title}</span>
                        </span>
                        <a
                            href={toLocalizedUrl('/frakt')}
                            className="btn btn-link text-primary ml-1">
                            {t('internationalShippingModal_readMore')}
                        </a>
                    </div>
                    <div className="text-left size-paris mt-1 d-flex align-items-center">
                        <strong className="mr-1">{buyerRegionText}</strong>
                        {props.internationalShipping.fromCountryCodeIso2 &&
                            props.internationalShipping.fromCountryCodeIso2 !==
                                props.shippingCountryCode && (
                                <>
                                    {t('vip_from_country_text', {
                                        shippingOptionCountry: t(
                                            mapCountryCodeToCountryKey(
                                                props.internationalShipping
                                                    .fromCountryCodeIso2
                                            )
                                        )
                                    })}
                                    <Flag
                                        className="icon icon-sm ml-1 align-middle"
                                        countryCodeIso2={
                                            props.internationalShipping
                                                .fromCountryCodeIso2
                                        }
                                    />
                                </>
                            )}
                    </div>
                </>
            }
            body={
                props.internationalShipping.providers.length ? (
                    <table
                        className={`${styles.internationalShippingModalTable} w-100`}>
                        <thead>
                            <tr>
                                <th scope="col" className="size-paris">
                                    {t(
                                        'internationalShippingModal_destination'
                                    )}
                                </th>
                                {props.internationalShipping.providers.map(
                                    provider => {
                                        return (
                                            <th key={provider} scope="col">
                                                {getProviderCell(provider, t)}
                                            </th>
                                        );
                                    }
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            <ShippingRow
                                key={ownCountryRow.toCountryCodeIso2}
                                row={ownCountryRow}
                                isOwnRow={true}
                                providers={
                                    props.internationalShipping.providers
                                }
                                messagingLink={
                                    props.internationalShipping.messagingLink
                                }
                                ownItem={props.internationalShipping.ownItem}
                                t={t}
                            />
                            {sortedRows.map(row => (
                                <ShippingRow
                                    key={row.toCountryCodeIso2}
                                    row={row}
                                    isOwnRow={false}
                                    providers={
                                        props.internationalShipping.providers
                                    }
                                    messagingLink={
                                        props.internationalShipping
                                            .messagingLink
                                    }
                                    ownItem={
                                        props.internationalShipping.ownItem
                                    }
                                    t={t}
                                />
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="d-flex justify-content-between align-items-center">
                        {t('internationalShippingModal_noShippingInfo')}
                        <span>
                            {getMessagingCell(
                                props.internationalShipping.messagingLink,
                                props.internationalShipping.ownItem,
                                t
                            )}
                        </span>
                    </div>
                )
            }
        />
    );
};

const Modal = ({ closeModal, header, body }) => (
    <DialogModal onDismiss={closeModal} className="test">
        <ModalContainer
            onClose={closeModal}
            className={styles.internationalShippingModalContainer}>
            <ModalHeader
                className={`${styles.internationalShippingModalHeader} border-bottom mt-0`}
                onClose={closeModal}>
                {header}
            </ModalHeader>
            <ModalBody
                className={`${styles.internationalShippingModalBody} justify-content-center px-5 pt-3 pb-3`}>
                {body}
            </ModalBody>
        </ModalContainer>
    </DialogModal>
);

const getMessagingCell = (
    { buyerId, sellerId, itemId, source, isLoggedIn, memberId },
    ownItem,
    t
) =>
    !isLoggedIn ? (
        <span className="font-italic">
            {t('internationalShippingModal_logIn')}
        </span>
    ) : ownItem ? (
        ''
    ) : (
        <MessagingButton
            buyerId={buyerId}
            sellerId={sellerId}
            itemId={itemId}
            source={source}
            isLoggedIn={isLoggedIn}
            memberId={memberId}
        />
    );

const getValueCell = (row, provider, isOwnRow, messagingLink, ownItem, t) => {
    const getEmptyCell = () =>
        isOwnRow ? getMessagingCell(messagingLink, ownItem, t) : '-';

    return row[provider] || row[provider] === 0 ? (
        <span>
            {t('internationalShippingModal_currency', {
                price: row[provider].shippingCost
            })}
            <span className="size-oslo text-gray-600">
                {row[provider].shippingWeight < 1
                    ? ` ${row[provider].shippingWeight * 1000} g`
                    : ` ${row[provider].shippingWeight} kg`}
            </span>
        </span>
    ) : (
        getEmptyCell()
    );
};

const getProviderIcon = provider => (
    <ShippingProviderIcon providerName={provider} iconClass="mr-1" />
);
const providerImageMap = {
    DHL: () => getProviderIcon('DHL'),
    BringDenmark: () => getProviderIcon('BringDenmark'),
    Bussgods: () => getProviderIcon('Bussgods'),
    Posten: () => getProviderIcon('Posten'),
    PostNordParcel: () => getProviderIcon('PostNordParcel'),
    PostNordStamp: () => getProviderIcon('PostNordStamp'),
    Schenker: () => getProviderIcon('Schenker'),
    SchenkerPrivpak: () => getProviderIcon('SchenkerPrivpak'),
    Alternative: t => t('internationalShippingModal_other')
};

const getProviderCell = (provider, t) =>
    providerImageMap[provider] ? providerImageMap[provider](t) : provider;

const removeDuplicateOwnCountryRowIfOnlyRow = (ownShippingCountryCode, rows) =>
    rows.length === 1 && rows[0].toCountryCodeIso2 === ownShippingCountryCode
        ? []
        : rows;

const ShippingRow = ({
    row,
    providers,
    t,
    isOwnRow,
    messagingLink,
    ownItem
}) => (
    <tr>
        <th scope="row" className="size-paris align-middle">
            <span>
                {row.country}
                {isOwnRow && (
                    <Flag
                        className={styles.internationalShippingModalBodyCountry}
                        countryCodeIso2={row.toCountryCodeIso2}
                    />
                )}
            </span>
        </th>
        {providers.map((provider, i) => {
            return (
                <td
                    key={`${row.toCountryCodeIso2}-${provider || i}`}
                    className="size-paris align-middle">
                    {getValueCell(
                        row,
                        provider,
                        isOwnRow,
                        messagingLink,
                        ownItem,
                        t
                    )}
                </td>
            );
        })}
    </tr>
);

const mapStateToProps = state => ({
    internationalShipping: state.internationalShipping,
    shippingCountryCode: selectShippingCountry(state)?.countryCodeIso2
});

const mapDispatchToProps = {
    closeModal
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InternationalShippingModal);

export { InternationalShippingModal };
